import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const QUALITY_COLORS = {
  good: (theme) => theme.palette.success.main,
  stale: (theme) => theme.palette.warning.main,
  invalid: (theme) => theme.palette.error.main,
  unavailable: (theme) => theme.palette.text.disabled,
};

function levelColor(percent, theme) {
  if (percent >= 60) return theme.palette.success.main;
  if (percent >= 25) return theme.palette.warning.main;
  return theme.palette.error.main;
}

export default function CircularGauge({
  value = 0,
  min = 0,
  max = 100,
  unit = "%",
  label = "",
  subLabel = "",
  quality = "good",
  size = 170,
  strokeWidth = 14,
  colorMode = "level", // 'level' | 'quality' | any CSS color string
}) {
  const v = clamp(Number(value) || 0, min, max);
  const percent = max > min ? (v - min) / (max - min) : 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percent);

  const color = useMemo(() => {
    if (colorMode === "quality") return (QUALITY_COLORS[quality] ?? QUALITY_COLORS.good)(theme);
    if (colorMode === "level") return levelColor(percent * 100, theme);
    return colorMode;
  }, [colorMode, quality, percent, theme]);

  const displayValue = unit === "%" ? Math.round(v) : Math.round(v * 100) / 100;

  const theme = useTheme();
  return (
    <div
      style={{
        background: theme.palette.background.default,
        borderRadius: 18,
        padding: 16,
        textAlign: "center",
        color: theme.palette.text.primary,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        minWidth: size + 32,
      }}
    >
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} stroke={theme.palette.text.secondary} strokeWidth={strokeWidth} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.3s ease" }}
        />
          <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" fontSize={size * 0.19} fontWeight="bold" fill={theme.palette.text.primary}>
          {displayValue}
          {unit}
        </text>
        {subLabel && (
          <text x="50%" y="63%" textAnchor="middle" dominantBaseline="middle" fontSize={size * 0.08} fill={theme.palette.text.disabled}>
            {subLabel}
          </text>
        )}
      </svg>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{label}</div>
      {subLabel && (
        <div style={{ fontSize: 11, color: QUALITY_COLORS[quality] ?? QUALITY_COLORS.good }}>
          {subLabel}
        </div>
      )}
    </div>
  );
}